import type { ComponentProps } from 'react';
import type { Badge } from '@cotera/client/v0/components/ui/badge';
import {
  resourceColorBadgeVariant,
  resourceColors,
  type ResourceColorKey,
} from '@cotera/client/app/v1/resources/type/resource-type-colors';

/**
 * Visual family a workflow card belongs to. Drives the icon-chip tint and the
 * type badge so triggers, tools, and agents are distinguishable at a glance
 * (not just by their icon).
 *
 * Accents that name a resource kind (`trigger`, `tool`, `agent`, `workflow`)
 * are derived from the shared resource palette so a node on the canvas matches
 * the same resource in folders, mentions, and the resource picker. `control`
 * and `neutral` are canvas-only concepts with no resource equivalent.
 */
export type NodeAccent =
  | 'trigger'
  | 'tool'
  | 'agent'
  | 'workflow'
  | 'dataset'
  | 'webhook'
  | 'event'
  | 'control'
  | 'neutral';

type BadgeVariant = ComponentProps<typeof Badge>['variant'];

export type NodeAccentStyle = {
  /** Tailwind classes for the rounded icon chip (background + icon color). */
  readonly chip: string;
  /** Badge variant for the type pill. */
  readonly badge: BadgeVariant;
};

const resourceAccent = (color: ResourceColorKey): NodeAccentStyle => ({
  chip: `${resourceColors[color].bg} ${resourceColors[color].text}`,
  badge: resourceColorBadgeVariant(color),
});

/**
 * Flow control and workflow events aren't resources, so they share the one
 * accent no resource colour claims: the app's primary blue. They read as a
 * single "wiring" family — the plumbing between steps rather than a thing you
 * own — which is why the two names resolve to the same style.
 */
const WIRING_ACCENT: NodeAccentStyle = {
  chip: 'bg-primary/10 text-primary',
  badge: 'primary',
};

export const NODE_ACCENTS: Record<NodeAccent, NodeAccentStyle> = {
  trigger: resourceAccent('trigger'),
  tool: resourceAccent('tool'),
  agent: resourceAccent('agent'),
  workflow: resourceAccent('workflow'),
  dataset: resourceAccent('dataset'),
  // Webhooks have no resource of their own; green is the only palette colour
  // no node type uses, so it stays unambiguous next to the other accents.
  webhook: resourceAccent('folder'),
  event: WIRING_ACCENT,
  control: WIRING_ACCENT,
  neutral: { chip: 'bg-secondary text-muted-foreground', badge: 'gray' },
};
